// src/components/PrivacyPolicy.js
import React from "react";
import { Container, Box, Typography, Paper, Button } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import PolicyContactSection from "./PolicyContactSection";

const PrivacyPolicy = ({ onBack, primaryBlue, glassStyles }) => {
  const defaultPrimaryBlue = primaryBlue || {
    50: "#F0F8FF",
    100: "#E6F0FA",
    500: "#1E40AF",
    600: "#002366",
    700: "#001A4D",
    800: "#0B1C48",
  };

  const defaultGlassStyles = glassStyles || {
    background: "rgba(255, 255, 255, 0.25)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.18)",
    borderRadius: "16px",
    boxShadow: "0 8px 32px rgba(11, 28, 72, 0.37)",
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${defaultPrimaryBlue[50]} 0%, ${defaultPrimaryBlue[100]} 100%)`,
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBack />}
          onClick={onBack}
          sx={{
            mb: 3,
            color: defaultPrimaryBlue[700],
            fontWeight: 600,
          }}
        >
          Back
        </Button>

        <Paper
          elevation={0}
          sx={{
            ...defaultGlassStyles,
            p: { xs: 3, md: 5 },
            bgcolor: "rgba(255, 255, 255, 0.9)",
          }}
        >
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: defaultPrimaryBlue[700],
              mb: 2,
            }}
          >
            Privacy Policy
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Last Updated: January 2025
          </Typography>

          <Box sx={{ "& > *": { mb: 3 } }}>
            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                1. Introduction
              </Typography>
              <Typography variant="body1" paragraph>
                Welcome to InfinityWhale ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.
              </Typography>
              <Typography variant="body1" paragraph>
                By using our website, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                2. Information We Collect
              </Typography>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                2.1 Personal Information
              </Typography>
              <Typography variant="body1" paragraph>
                We collect personal information that you voluntarily provide to us when you:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Register for an account<br />
                • Place an order<br />
                • Subscribe to our newsletter<br />
                • Contact customer support<br />
                • Participate in our referral program
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                This information may include:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Full name<br />
                • Email address<br />
                • Phone number<br />
                • Shipping and billing address<br />
                • Payment information (processed securely through payment gateways)<br />
                • UPI ID or bank account details for refunds<br />
                • Date of birth<br />
                • Referral code and referral relationships
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                2.2 Automatically Collected Information
              </Typography>
              <Typography variant="body1" paragraph>
                When you visit our website, we automatically collect certain information about your device, including:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • IP address<br />
                • Browser type and version<br />
                • Operating system<br />
                • Pages visited and time spent<br />
                • Referring website<br />
                • Device identifiers
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                3. How We Use Your Information
              </Typography>
              <Typography variant="body1" paragraph>
                We use the information we collect for the following purposes:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • <strong>Order Processing:</strong> To process and fulfill your orders, including payment processing, shipping, and delivery<br />
                • <strong>Account Management:</strong> To create and manage your account, including authentication and security<br />
                • <strong>Customer Service:</strong> To respond to your inquiries, provide support, and resolve issues<br />
                • <strong>Referral Program:</strong> To manage referral relationships, track commissions, and distribute rewards<br />
                • <strong>Marketing:</strong> To send promotional emails, newsletters, and special offers (with your consent)<br />
                • <strong>Analytics:</strong> To analyze usage patterns and improve our website and services<br />
                • <strong>Legal Compliance:</strong> To comply with legal obligations, including tax and GST requirements<br />
                • <strong>Fraud Prevention:</strong> To detect and prevent fraudulent transactions and protect our users
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                4. Information Sharing and Disclosure
              </Typography>
              <Typography variant="body1" paragraph>
                We do not sell your personal information. We may share your information with:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • <strong>Payment Processors:</strong> To process payments securely (Razorpay, UPI, etc.)<br />
                • <strong>Shipping Partners:</strong> To deliver your orders<br />
                • <strong>Email Service Providers:</strong> To send transactional and marketing emails<br />
                • <strong>Analytics Providers:</strong> To analyze website usage (Google Analytics, Firebase)<br />
                • <strong>Legal Authorities:</strong> When required by law or to protect our rights<br />
                • <strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                5. Data Security
              </Typography>
              <Typography variant="body1" paragraph>
                We implement appropriate technical and organizational security measures to protect your personal information, including:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • SSL/TLS encryption for data transmission<br />
                • Secure Firebase authentication and database<br />
                • Regular security audits and updates<br />
                • Access controls and authentication<br />
                • Secure payment processing through certified gateways
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                However, no method of transmission over the internet is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                6. Your Privacy Rights
              </Typography>
              <Typography variant="body1" paragraph>
                You have the following rights regarding your personal information:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • <strong>Access:</strong> Request a copy of your personal information<br />
                • <strong>Correction:</strong> Update or correct inaccurate information<br />
                • <strong>Deletion:</strong> Request deletion of your account and data<br />
                • <strong>Opt-out:</strong> Unsubscribe from marketing communications<br />
                • <strong>Data Portability:</strong> Request your data in a portable format<br />
                • <strong>Withdraw Consent:</strong> Withdraw consent for data processing
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                To exercise these rights, contact us at support@infinitywhale.com
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                7. Cookies and Tracking Technologies
              </Typography>
              <Typography variant="body1" paragraph>
                We use cookies and similar tracking technologies to enhance your experience. Cookies are small data files stored on your device. We use:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • <strong>Essential Cookies:</strong> Required for website functionality<br />
                • <strong>Analytics Cookies:</strong> To understand how you use our website<br />
                • <strong>Marketing Cookies:</strong> To deliver relevant advertisements<br />
                • <strong>Session Cookies:</strong> To maintain your login session
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                You can control cookies through your browser settings, but disabling them may affect website functionality.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                8. Data Retention
              </Typography>
              <Typography variant="body1" paragraph>
                We retain your personal information for as long as necessary to:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Fulfill the purposes outlined in this policy<br />
                • Comply with legal obligations (tax records: 7 years)<br />
                • Resolve disputes and enforce agreements<br />
                • Maintain referral program records
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                After this period, we will securely delete or anonymize your information.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                9. Children's Privacy
              </Typography>
              <Typography variant="body1" paragraph>
                Our services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                10. Third-Party Links
              </Typography>
              <Typography variant="body1" paragraph>
                Our website may contain links to third-party websites. We are not responsible for the privacy practices of these websites. We encourage you to review their privacy policies.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                11. Changes to This Policy
              </Typography>
              <Typography variant="body1" paragraph>
                We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the new policy on this page and updating the "Last Updated" date. Your continued use of our services after changes constitutes acceptance of the updated policy.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                12. Contact Us
              </Typography>
              <PolicyContactSection intro="If you have questions or concerns about this Privacy Policy, please contact us:" />
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                13. Governing Law
              </Typography>
              <Typography variant="body1" paragraph>
                This Privacy Policy is governed by the laws of India, including the Information Technology Act, 2000 and the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011.
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
